import React, { useState, useEffect, createContext } from "react";
import { useNavigate, Routes, Route, Link, useLocation } from "react-router-dom";
import "./Admin.scss";
import Login from "./Login";
import Dashboard from "./Dashboard";
import ChangePassword from "./ChangePassword";
import PropertiesManager from "./PropertiesManager";
import PropertyEditor from "./PropertyEditor";
import GalleryManager from "./GalleryManager";
import { api } from "../../api";

export const AuthContext = React.createContext(null);
export const LoadingContext = createContext(null);

const Admin = () => {
    const [isAuthenticated, setIsAuthenticated] = useState(false);
    const [token, setToken] = useState(
        localStorage.getItem("ellington_cms_token")
    );
    const [user, setUser] = useState(null);
    const [isLoading, setIsLoading] = useState(true);
    const [isPageTransition, setIsPageTransition] = useState(false);
    const navigate = useNavigate();
    const location = useLocation();

    useEffect(() => {
        const checkAuth = async () => {
            if (token) {
                try {
                    api.defaults.headers.common["Authorization"] = `Bearer ${token}`;
                    const response = await api.get("/auth/me");
                    
                    if (response.data && response.data.username) {
                        setIsAuthenticated(true);
                        setUser(response.data);
                    } else {
                        // Invalid response, clear token
                        localStorage.removeItem("ellington_cms_token");
                        delete api.defaults.headers.common["Authorization"];
                        setToken(null);
                        setIsAuthenticated(false);
                        setUser(null);
                    }
                } catch (error) {
                    console.error("Authentication check failed:", error);
                    // Token invalid or expired
                    localStorage.removeItem("ellington_cms_token");
                    delete api.defaults.headers.common["Authorization"];
                    setToken(null);
                    setIsAuthenticated(false);
                    setUser(null);
                } finally {
                    setIsLoading(false);
                }
            } else {
                setIsLoading(false);
            }
        };

        checkAuth();
    }, [token]);

    // Don't show loading indicator during page transitions
    useEffect(() => {
        setIsPageTransition(false);
    }, [location.pathname]);

    const login = (newToken) => {
        localStorage.setItem("ellington_cms_token", newToken);
        api.defaults.headers.common["Authorization"] = `Bearer ${newToken}`;
        setToken(newToken);
        setIsAuthenticated(true);
        navigate("/admin/dashboard");
    };

    const logout = () => {
        localStorage.removeItem("ellington_cms_token");
        delete api.defaults.headers.common["Authorization"];
        setToken(null);
        setIsAuthenticated(false);
        setUser(null);
        navigate("/admin");
    };

    // Only show loading on initial auth check, not during page transitions
    if (isLoading && !isAuthenticated && !isPageTransition) {
        return (
            <div className="admin-container">
                <div className="admin-content">
                    <div className="content-loading">Loading...</div>
                </div>
            </div>
        );
    }

    // Helper function to check if a link is active
    const isActive = (path) => {
        return location.pathname.startsWith(`/admin${path}`);
    };

    return (
        <AuthContext.Provider value={{ isAuthenticated, user, login, logout }}>
            <LoadingContext.Provider value={{ isPageTransition, setIsPageTransition }}>
                <div className="admin-container">
                    {isAuthenticated && (
                        <nav className="admin-nav">
                            <div className="admin-logo">Ellington CMS</div>
                            <ul className="admin-menu">
                                <li>
                                    <Link 
                                        to="/admin/dashboard" 
                                        className={isActive("/dashboard") ? "active" : ""}
                                    >
                                        Dashboard
                                    </Link>
                                </li>
                                <li>
                                    <Link 
                                        to="/admin/properties" 
                                        className={isActive("/properties") ? "active" : ""}
                                    >
                                        Properties
                                    </Link>
                                </li>
                                <li>
                                    <Link 
                                        to="/admin/change-password" 
                                        className={isActive("/change-password") ? "active" : ""}
                                    >
                                        Change Password
                                    </Link>
                                </li>
                                <li>
                                    <button onClick={logout} className="logout-btn">
                                        Logout
                                    </button>
                                </li>
                            </ul>
                        </nav>
                    )}
                    <div className="admin-content">
                        <Routes>
                            <Route
                                path="/"
                                element={
                                    !isAuthenticated ? <Login /> : <Dashboard />
                                }
                            />
                            <Route
                                path="/dashboard"
                                element={
                                    isAuthenticated ? <Dashboard /> : <Login />
                                }
                            />
                            <Route
                                path="/properties"
                                element={
                                    isAuthenticated ? <PropertiesManager /> : <Login />
                                }
                            />
                            <Route
                                path="/properties/:itemType/:itemId"
                                element={
                                    isAuthenticated ? <PropertyEditor /> : <Login />
                                }
                            />
                            <Route
                                path="/properties/:itemType/:itemId/gallery"
                                element={
                                    isAuthenticated ? <GalleryManager /> : <Login />
                                }
                            />
                            <Route
                                path="/change-password"
                                element={
                                    isAuthenticated ? <ChangePassword /> : <Login />
                                }
                            />
                        </Routes>
                    </div>
                </div>
            </LoadingContext.Provider>
        </AuthContext.Provider>
    );
};

export default Admin;
